import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Swal from "sweetalert2";
import {
  formateDateWithMonth,
  handleServerValidations,
} from "../../../../utils/commonfunction";
import Sorting from "../../common/sorting";
import Loader from "../../common/loader";
import Breadcrums from "../../common/breadcrumbs";
import CustomPagination from "../../common/custompagination";
import { SWAL_SETTINGS } from "../../../../utils/Constants";
import {
  adminNotificationList,
  broadcastAdminNotification,
} from "../../services/notification.services";

const NotificationTable = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [list, setList] = useState([]);
  const [datalength, setDataLength] = useState(0);
  const [itemperpage] = useState(10);
  const [sorting, setSorting] = useState({});
  const [defaultSorting, setDefaultSorting] = useState(true);
  const [page, setPage] = useState(1);
  const [loader, setLoader] = useState(true);
  const [globalsearch, setGlobalSearch] = useState("");
  const [listRefresh, setListRefresh] = useState(0);

  const breadcrumbs = [
    { title: t("link_dashboard"), url: "/admin/dashboard" },
    { title: "Notifications", url: "" },
  ];

  useEffect(() => {
    const getData = setTimeout(() => {
      setLoader(true);

      adminNotificationList({
        page,
        per_page: itemperpage,
        sort: JSON.stringify(sorting),
        global_search: globalsearch,
      })
        .then((data) => {
          if (data?.success) {
            setDataLength(data.data?.total_records ?? 0);
            setList(data.data?.list ?? []);
            setPage(data.data?.page ?? page);
          } else {
            setDataLength(0);
            setList([]);
          }
          setLoader(false);
        })
        .catch((error) => {
          console.log("error ====> ", error);
          setDataLength(0);
          setList([]);
          setLoader(false);
        });
    }, 300);
    return () => clearTimeout(getData);
  }, [location, sorting, globalsearch, itemperpage, page, listRefresh]);

  const handleSort = (e, column) => {
    setDefaultSorting(false);
    let sort = { order: 0, column: column };
    if (e.target.classList.contains("assc")) {
      sort.order = -1;
    } else {
      sort.order = 1;
    }
    setSorting(sort);
    setPage(1);
  };

  const resetFilter = (e) => {
    e.preventDefault();
    setGlobalSearch("");
    setPage(1);
  };

  const handleBroadcast = (id) => {
    Swal.fire({
      title: "Send notification now?",
      text: "This will broadcast the selected notification.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#403fad",
      cancelButtonColor: "#f1388b",
      confirmButtonText: "Yes, Send",
    }).then((result) => {
      if (!result.isConfirmed) return;
      broadcastAdminNotification({ id: String(id) })
        .then((response) => {
          if (response?.success) {
            if (response?.data) {
              setList((prev) =>
                prev.map((item) =>
                  item.id === response.data.id
                    ? { ...item, ...response.data }
                    : item,
                ),
              );
            }
            setListRefresh((r) => r + 1);
            Swal.fire({
              icon: "success",
              text: response?.message || "Notification broadcast successfully.",
              ...SWAL_SETTINGS,
            });
          } else {
            Swal.fire({
              icon: "error",
              text: handleServerValidations(response),
              ...SWAL_SETTINGS,
            });
          }
        })
        .catch((error) => {
          Swal.fire({
            icon: "error",
            text:
              error?.response?.data?.message ||
              "Something went wrong while broadcasting notification.",
            ...SWAL_SETTINGS,
          });
        });
    });
  };

  return (
    <>
      <Breadcrums data={breadcrumbs} />
      <div className="animation_fade">
        <div className="card custom-card">
          <div className="card-body">
            <div className="d-flex justify-content-between align-items-center mb-4">
              <h6 className="main-content-label">Notifications</h6>
              <div className="d-flex align-items-center">
                <button
                  className="btn ripple btn-main-primary signbtn mr-2"
                  onClick={() => navigate(`/admin/notification/send`)}
                >
                  Create Notification
                </button>
              </div>
            </div>

            <div className="table-responsive">
              <table className="table table-bordered border-t0 key-buttons text-nowrap w-100">
                <thead>
                  <tr>
                    <th className="position-relative select_head">
                      <span>{t("list_heading_sno")}</span>
                    </th>
                    <th>
                      <div className="sorting_column">
                        <span>Title</span>
                        <Sorting
                          sort={sorting}
                          handleSort={handleSort}
                          column="title"
                        />
                      </div>
                    </th>
                    <th>Message</th>
                    <th>Sender Type</th>
                    <th>Broadcast</th>
                    <th className="created_head">
                      <div className="sorting_column">
                        <span>{t("list_heading_created_date")}</span>
                        <Sorting
                          sort={sorting}
                          handleSort={handleSort}
                          defaultSorting={defaultSorting}
                          column="createdAt"
                        />
                      </div>
                    </th>
                    <th className="action_head">{t("list_heading_action")}</th>
                  </tr>
                </thead>
                <tbody>
                  {loader ? (
                    <tr>
                      <td colSpan={7}>
                        <Loader />
                      </td>
                    </tr>
                  ) : list.length ? (
                    list.map((row, i) => (
                      <tr key={row.id ?? i}>
                        <td>{(page - 1) * itemperpage + i + 1}</td>
                        <td>{row?.title || "N/A"}</td>
                        <td>
                          <span
                            title={row?.message}
                            style={{
                              display: "inline-block",
                              maxWidth: "280px",
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                            }}
                          >
                            {row?.message || "N/A"}
                          </span>
                        </td>
                        <td>
                          <span className="badge bg-primary">
                            {row?.sender_type || "N/A"}
                          </span>
                        </td>
                        <td>
                          <span
                            className={`badge bg-${
                              row?.is_broadcast ? "success" : "secondary"
                            }`}
                          >
                            {row?.is_broadcast ? "Yes" : "No"}
                          </span>
                        </td>
                        <td>
                          {row?.createdAt
                            ? formateDateWithMonth(row.createdAt)
                            : "N/A"}
                        </td>
                        <td>
                          <div className="d-flex">
                            <button
                              className="btn ripple btn-main-primary signbtn"
                              onClick={() =>
                                navigate(`/admin/notification/view/${row?.id}`)
                              }
                            >
                              {/* View */}
                              <i className="fa fa-eye me-1"></i>
                              {t("btn_view")}
                            </button>
                            <button
                              className="btn ripple btn-success mlAction"
                              onClick={() =>
                                navigate(`/admin/notification/edit/${row?.id}`)
                              }
                            >
                              {/* Edit */}
                              <i className="fa fa-edit me-1"></i>
                              {t("btn_edit")}
                            </button>
                            {!row?.is_broadcast && (
                              <button
                                className="btn ripple mlAction"
                                style={{
                                  backgroundColor: "#403fad",
                                  color: "#fff",
                                  border: "1px solid #403fad",
                                }}
                                onClick={() => handleBroadcast(row?.id)}
                              >
                                Send Notification
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={7} className="text-center">
                        {t("msg_no_records")}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <div className="mt-3">
              <b>
                {t("msg_total_records")} : {datalength ?? 0}
              </b>
            </div>
            {datalength > 0 ? (
              <CustomPagination
                datalength={datalength}
                itemperpage={itemperpage}
                currentPage={page}
                setPage={setPage}
                modalPagination
              />
            ) : null}
          </div>
        </div>
      </div>
    </>
  );
};

export default NotificationTable;
